/**
 * Collection 계층: items + pinned를 dedupe해 렌더 그룹과 키보드 탐색 순서를 만든다.
 */

export type ListViewEntry<T> = {
  item: T;
  key: string;
  /**
   * onSelect·context가 쓰는 소비처 index 네임스페이스: 정규 행은 `items` 인덱스,
   * pinned 행은 `pinned.items`의 **원래** 인덱스다 — 결과와 중복돼 제거된 항목이
   * 있어도 유지되어 소비처 역참조(`pinned.items[index]`)가 어긋나지 않는다.
   */
  index: number;
  /** 렌더된 그룹 안 순서(dedupe 후 0..N-1). ARIA 위치·마지막 행 판단에 쓴다. */
  order: number;
  pinned: boolean;
};

export function useListViewCollections<T>({
  items,
  getKey,
  pinnedItems,
  regularNavigable,
}: {
  items: readonly T[];
  getKey: (item: T) => string;
  pinnedItems: readonly T[];
  /**
   * 정규 행을 키보드 탐색 대상에 포함할지. 로드 전(status !== "success")에는
   * 정규 행이 mount되지 않으므로 false — 미마운트 행을 선택하면 pending focus가
   * 잔류해 나중에 그 행이 mount되는 순간 무관한 element에서 focus를 훔친다.
   */
  regularNavigable: boolean;
}): {
  pinnedEntries: ListViewEntry<T>[];
  regularEntries: ListViewEntry<T>[];
  navigationEntries: ListViewEntry<T>[];
} {
  const resultKeys = new Set(items.map(getKey));
  const seenPinnedKeys = new Set<string>();
  const pinnedEntries: ListViewEntry<T>[] = [];
  pinnedItems.forEach((item, sourceIndex) => {
    const key = getKey(item);
    if (resultKeys.has(key) || seenPinnedKeys.has(key)) return;
    seenPinnedKeys.add(key);
    pinnedEntries.push({
      item,
      key,
      index: sourceIndex,
      order: pinnedEntries.length,
      pinned: true,
    });
  });
  const regularEntries = items.map<ListViewEntry<T>>((item, index) => ({
    item,
    key: getKey(item),
    index,
    order: index,
    pinned: false,
  }));
  return {
    pinnedEntries,
    regularEntries,
    navigationEntries: regularNavigable
      ? [...pinnedEntries, ...regularEntries]
      : [...pinnedEntries],
  };
}
